import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellIcon, CheckCheckIcon, ExternalLinkIcon, MailIcon } from 'lucide-react';
import {
  Button,
  DataTable,
  EmptyState,
  ErrorNotice,
  LoadingState,
  PageHeading,
  Panel,
  Pill,
  SearchInput,
  type DataTableSort,
  type PillTone,
} from '@bcecloud/portal-ui';
import { toast } from 'sonner';
import { Checkbox } from '@/components/ui/checkbox';
import { errorMessage } from '@/lib/api';
import { filterRows, sortRows } from '@/lib/facet-table';
import { useTableSort } from '@/lib/use-table-sort';
import { cn } from '@/lib/utils';
import {
  NOTIFICATION_KIND_GROUPS,
  NOTIFICATION_KIND_HINTS,
  NOTIFICATION_KIND_LABELS,
  channelsFor,
  isExternalHref,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
  readNotificationPreferences,
  relativeTime,
  saveNotificationPreferences,
  subjectHref,
  type KindChannels,
  type Notification,
  type NotificationKind,
} from '@/lib/notifications';

const SORT_FIELDS = { when: 'createdMs', what: 'title', kind: 'kindLabel' } as const;
const SORT_KEYS = Object.values(SORT_FIELDS);
const DEFAULT_SORT: DataTableSort = { key: SORT_FIELDS.when, direction: 'desc' };

/** The colour a kind wears in the feed, so a scan finds the shipments. */
const KIND_TONES: Readonly<Record<NotificationKind, PillTone>> = {
  'note.created': 'brand',
  'note.updated': 'brand',
  'shipment.created': 'warning',
  'shipment.updated': 'warning',
  'shipment.return-label-unused': 'warning',
  'event.date': 'danger',
  'event.venue': 'danger',
  'event.facilitator': 'danger',
  'event.users': 'neutral',
  'message.mention': 'success',
};

interface NotificationRow extends Notification {
  readonly createdMs: number;
  readonly kindLabel: string;
}

function notificationRow(notification: Notification): NotificationRow {
  return {
    ...notification,
    createdMs: Date.parse(notification.createdAt),
    kindLabel: NOTIFICATION_KIND_LABELS[notification.kind] ?? notification.kind,
  };
}

/**
 * Everything that happened on your events, and how you want to hear about
 * each kind of it.
 */
export function NotificationsPage(): React.ReactElement {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const { sort, onSort } = useTableSort(SORT_KEYS, DEFAULT_SORT);
  const feed = useQuery({
    queryKey: ['notifications', 'all'],
    queryFn: ({ signal }) => listNotifications({ limit: 200 }, signal),
  });
  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['notifications'] });
  }
  const readOne = useMutation({ mutationFn: (id: string) => markNotificationRead(id), onSettled: refresh });
  const readAll = useMutation({
    mutationFn: () => markAllNotificationsRead(),
    onSuccess: async () => {
      toast.success('All caught up');
      await refresh();
    },
  });
  const rows = feed.data
    ? sortRows(filterRows(feed.data.rows.map(notificationRow), search, ['title', 'body', 'kindLabel']), sort)
    : [];
  const unread = feed.data?.unread ?? 0;

  return (
    <section className="animate-portal-rise">
      <PageHeading
        eyebrow="YOU"
        title="Notifications"
        subtitle="What changed on the events you are on, and how you want to hear about each kind of change."
        action={
          unread > 0 ? (
            <Button variant="secondary" onClick={() => readAll.mutate()} disabled={readAll.isPending}>
              <CheckCheckIcon aria-hidden className="size-4" /> Mark all read
            </Button>
          ) : undefined
        }
      />
      <div className="mt-6 space-y-8">
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <h2 className="text-portal-card-title font-bold">
              Activity
              {unread > 0 ? <Pill tone="brand" className="ml-2 align-middle">{unread} unread</Pill> : null}
            </h2>
            <SearchInput
              label="Search notifications"
              placeholder="Event, note or shipment"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              className="w-full max-w-sm"
            />
          </div>
          {feed.isPending ? (
            <LoadingState label="Loading notifications…" />
          ) : feed.isError ? (
            <ErrorNotice title="Notifications unavailable" message={errorMessage(feed.error)} />
          ) : rows.length === 0 ? (
            <Panel>
              <EmptyState
                icon={<BellIcon aria-hidden className="size-6" />}
                title="Nothing yet"
                message="Notes, shipments, event changes and client messages on your events show up here as they happen."
              />
            </Panel>
          ) : (
            <DataTable
              headers={[
                { label: 'When', sortKey: SORT_FIELDS.when },
                { label: 'What changed', sortKey: SORT_FIELDS.what },
                { label: 'Kind', sortKey: SORT_FIELDS.kind },
                'Actions',
              ]}
              visuallyHiddenHeaders={[3]}
              sort={sort}
              onSort={onSort}
            >
              {rows.map((row) => {
                const isUnread = !row.readAt;
                const target = row.href ?? subjectHref(row);
                return (
                  <tr
                    key={row._id}
                    className={cn(
                      'border-t border-portal-line align-top',
                      isUnread ? 'border-l-[3px] border-l-brand bg-white' : 'bg-portal-sky/40',
                    )}
                  >
                    <td className="p-3 text-portal-small whitespace-nowrap">
                      <span className={cn('block', isUnread ? 'font-semibold text-portal-ink' : 'text-portal-muted')}>
                        {relativeTime(row.createdAt)}
                      </span>
                      {row.emailedAt ? (
                        <span className="mt-0.5 flex items-center gap-1 text-portal-micro text-portal-muted">
                          <MailIcon aria-hidden className="size-3" /> Emailed
                        </span>
                      ) : null}
                    </td>
                    <td className="p-3 text-portal-small">
                      {isExternalHref(target) ? (
                        <a
                          href={target}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={() => (isUnread ? readOne.mutate(row._id) : undefined)}
                          className={cn('inline-flex items-center gap-1 text-portal-ink hover:text-brand hover:underline', isUnread && 'font-bold')}
                        >
                          {row.title}
                          <ExternalLinkIcon aria-hidden className="size-3.5 text-portal-muted" />
                        </a>
                      ) : (
                        <Link
                          to={target}
                          onClick={() => (isUnread ? readOne.mutate(row._id) : undefined)}
                          className={cn('block text-portal-ink hover:text-brand hover:underline', isUnread && 'font-bold')}
                        >
                          {row.title}
                        </Link>
                      )}
                      {row.body ? (
                        <span className={cn('mt-0.5 block text-portal-small', isUnread ? 'text-portal-ink' : 'text-portal-muted')}>
                          {row.body}
                        </span>
                      ) : null}
                    </td>
                    <td className="p-3 text-portal-small whitespace-nowrap">
                      <Pill tone={KIND_TONES[row.kind] ?? 'neutral'}>{row.kindLabel}</Pill>
                    </td>
                    <td className="p-3 text-right whitespace-nowrap">
                      {isUnread ? (
                        <Button variant="secondary" size="compact" onClick={() => readOne.mutate(row._id)}>
                          Mark read
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </DataTable>
          )}
        </div>

        <PreferencesPanel />
      </div>
    </section>
  );
}

/**
 * One row per kind of change, with its two switches. Saves on every change;
 * the row you touched is the only one sent, so nothing else moves.
 */
function PreferencesPanel(): React.ReactElement {
  const queryClient = useQueryClient();
  const preferences = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: ({ signal }) => readNotificationPreferences(signal),
  });
  const save = useMutation({
    mutationFn: (kinds: Partial<Record<NotificationKind, KindChannels>>) => saveNotificationPreferences(kinds),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  if (preferences.isPending) return <LoadingState label="Loading preferences…" />;
  if (preferences.isError) {
    return <ErrorNotice title="Preferences unavailable" message={errorMessage(preferences.error)} />;
  }
  const current = preferences.data;
  const mutedEvents = Object.entries(current.overrides).filter(([, override]) => override.muted);

  function set(kind: NotificationKind, channel: keyof KindChannels, on: boolean): void {
    save.mutate({ [kind]: { ...channelsFor(current, kind), [channel]: on } });
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-portal-card-title font-bold">How you hear about each kind of change</h2>
        <p className="mt-1 text-portal-small text-portal-muted">
          In the app is the bell in the top bar. Switch both off to hear nothing about that kind. You never get
          your own changes back, and emails from the legacy portal follow the same choices.
        </p>
      </div>
      <DataTable headers={['Tell me when', 'In the app', 'By email']} rightAlignedHeaders={[1, 2]}>
        {NOTIFICATION_KIND_GROUPS.map((group) => (
          <GroupRows key={group.label} label={group.label} kinds={group.kinds} current={current} busy={save.isPending} onChange={set} />
        ))}
      </DataTable>
      {mutedEvents.length > 0 ? (
        <p className="text-portal-small text-portal-muted">
          {mutedEvents.length === 1 ? 'One event is muted' : `${mutedEvents.length} events are muted`} on top of these
          choices. Unmute from its own page.
        </p>
      ) : null}
    </div>
  );
}

function GroupRows({
  label,
  kinds,
  current,
  busy,
  onChange,
}: {
  readonly label: string;
  readonly kinds: readonly NotificationKind[];
  readonly current: { readonly kinds: Partial<Record<NotificationKind, KindChannels>> };
  readonly busy: boolean;
  readonly onChange: (kind: NotificationKind, channel: keyof KindChannels, on: boolean) => void;
}): React.ReactElement {
  return (
    <>
      <tr className="border-t border-portal-line bg-portal-sky/40">
        <th scope="rowgroup" colSpan={3} className="px-3 py-2 text-left text-portal-micro font-extrabold tracking-[1px] text-portal-muted uppercase">
          {label}
        </th>
      </tr>
      {kinds.map((kind) => {
        const channels = current.kinds[kind] ?? { inApp: true, email: true };
        const off = !channels.inApp && !channels.email;
        return (
          <tr key={kind} className="border-t border-portal-line align-middle">
            <td className="p-3">
              <span className={cn('block text-portal-small font-semibold', off ? 'text-portal-muted' : 'text-portal-ink')}>
                {NOTIFICATION_KIND_LABELS[kind]}
                {off ? <Pill tone="neutral" className="ml-2 align-middle">Off</Pill> : null}
              </span>
              <span className="block text-portal-micro text-portal-muted">{NOTIFICATION_KIND_HINTS[kind]}</span>
            </td>
            <td className="p-3 text-right">
              <Checkbox
                aria-label={`${NOTIFICATION_KIND_LABELS[kind]}: in the app`}
                checked={channels.inApp}
                disabled={busy}
                onCheckedChange={(value) => onChange(kind, 'inApp', value === true)}
              />
            </td>
            <td className="p-3 text-right">
              <Checkbox
                aria-label={`${NOTIFICATION_KIND_LABELS[kind]}: by email`}
                checked={channels.email}
                disabled={busy}
                onCheckedChange={(value) => onChange(kind, 'email', value === true)}
              />
            </td>
          </tr>
        );
      })}
    </>
  );
}
